import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { isNativeShell } from "@/lib/oauthRedirect";

const DEBOUNCE_MS = 30 * 60 * 1000; // 30 minutes
const STORAGE_PREFIX = "grounded:lastSessionLoggedAt:";

/**
 * Logs an `app_sessions` row for the current user, debounced to once per
 * 30 minutes per user (across reloads, tab focus, and native resumes).
 *
 * No-ops when there's no authenticated user. Insert failures are swallowed —
 * analytics must never block UX.
 */
export function useSessionTracking(userId: string | null) {
  useEffect(() => {
    if (!userId) return;

    const key = `${STORAGE_PREFIX}${userId}`;

    const maybeLog = async () => {
      try {
        const last = Number(localStorage.getItem(key) || 0);
        if (Date.now() - last < DEBOUNCE_MS) return;

        // Mark optimistically to prevent races (focus + mount firing together).
        localStorage.setItem(key, String(Date.now()));

        const platform = isNativeShell() ? "ios" : "web";
        const userAgent = (navigator.userAgent || "").slice(0, 500);

        const { error } = await supabase.from("app_sessions").insert({
          user_id: userId,
          platform,
          user_agent: userAgent,
        });

        if (error) {
          console.warn("[session-tracking] insert failed:", error);
        }
      } catch (e) {
        console.warn("[session-tracking] unexpected error:", e);
      }
    };

    // Log on mount.
    maybeLog();

    // Re-evaluate when the app becomes visible again (tab focus, iOS resume).
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        maybeLog();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userId]);
}
